import React, { useState, useEffect, useRef  } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  Badge,
  Flex,
  useToast,
  useColorModeValue,
  Spinner,
  Button,
  Icon,
  Image,
  HStack,
  Spacer,
  useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,ModalFooter, Grid,
} from "@chakra-ui/react";
import axios from "axios";
import {  StudentAccountInfo } from '../../../Platform/Common/types';
import { FiClock  } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import eduviclogo from "../../../resources/Eduvictechlogo.png";
import { FaClock, FaHourglassStart, FaFlag } from "react-icons/fa";
import { toUtcISOString, parseUtc, fmtLocal } from "../../../utils/dates";


const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "green",
  SKIPPED: "red",
  IN_PROGRESS: "purple",
  NOT_STARTED: "gray",
  LOCKED: "gray",
  EXPIRED: "orange",
};

// Color scheme constants
const TOP_BAR_BG = { light: "gray.100", dark: "gray.700" };
const QUESTION_CARD_BG = { light: "blue.700", dark: "blue.700" };
const STATUS_BADGE_BG = { light: "blackAlpha.700", dark: "whiteAlpha.50" };
const TEXT_COLORS = { light: "gray.800", dark: "white" };




const StudentAttemptInstructionStart = () => {
    const navigate = useNavigate();
  
    const [searchParams] = useSearchParams();

const entitlementId =
  Number(searchParams.get("entitlement_id"));

const practiceSetId =
  Number(searchParams.get("practice_set_id"));

const assessmentType =
  searchParams.get("assessment_type");

const timerEnabled =
  searchParams.get("timer_enabled") === "true";

const questionSetId =
  searchParams.get("question_set_id");

const practiceSetExamId =
  searchParams.get("practice_set_exam_id");
    
  const [currentStudent, setCurrentStudent] = useState<StudentAccountInfo>();
  const [activeAttempt, setActiveAttempt] = useState<any>(null);
  const [activeLoaded, setActiveLoaded] = useState(false);


  const [loading, setLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const token = localStorage.getItem("b2c_token");
  const isAuthenticated = Boolean(token);

  const toast = useToast();
  const cardBg = useColorModeValue("gray.100", "gray.700");

  const apiUrl = process.env.REACT_APP_API_URL;


  /* ========================================================= */
/* DERIVED FLAGS */
/* ========================================================= */

const isSameAttemptInProgress =
  !!activeAttempt &&
  activeAttempt.practice_set_id === practiceSetId &&
  activeAttempt.assessment_type === assessmentType &&
  activeAttempt.timed_flag === timerEnabled &&
  (
    (
      assessmentType === "MOCK_EXAM" &&
      activeAttempt.practice_set_exam_id ===
        (practiceSetExamId ? Number(practiceSetExamId) : null)
    ) ||
    (
      assessmentType === "QUESTION_SET" &&
      activeAttempt.question_set_id ===
        (questionSetId ? Number(questionSetId) : null)
    )
  );


const hasAnotherAttemptInProgress =
  activeAttempt &&
  !isSameAttemptInProgress;

const assessmentLabel =
  assessmentType === "MOCK_EXAM"
    ? "Mock Exam"
    : timerEnabled
    ? "Timed Practice Test"
    : "Untimed Practice Test";

/* ========================================================= */
/* ACTION HANDLERS */
/* ========================================================= */

const handleStartOrResume = async () => {
  try {
    const payload = {
      entitlement_id: entitlementId,
      assessment_type: assessmentType,
      timer_enabled: timerEnabled,
      practice_set_exam_id:
        practiceSetExamId
          ? Number(practiceSetExamId)
          : null,
      question_set_id:
        questionSetId
          ? Number(questionSetId)
          : null,
    };

    console.log("Attempt Payload", payload);

    const response = await axios.post(
      `${apiUrl}/b2c/practice/${practiceSetId}/assessment-attempt/start`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const practice_attempt_id =
      response.data.practice_attempt_id;

    const url =
      `${window.location.origin}/mock-exam-window/${practice_attempt_id}`;

    window.location.href = url;

  } catch (error) {
    console.error(error);
  }
};

const handleResumeOtherAttempt = () => {
  if (!activeAttempt) return;

  const url =
    `${window.location.origin}/mock-exam-window/${activeAttempt.practice_attempt_id}`;

  window.location.href = url;
};



  const fetchCurrentStudent = async () => {
    try {
      const response = await axios.get(`${apiUrl}/b2c/student/account-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

const fetchActiveAttempt = async (entitlementId: number) => {
  try {
    const response = await axios.get(
      `${apiUrl}/b2c/entitlement/${entitlementId}/active-attempt`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
    );
    setActiveAttempt(response.data || null);
  } catch (error) {
    // No active attempt (or request failed): don't keep a stale one, e.g. after
    // the student submitted the attempt and pressed Back
    setActiveAttempt(null);
  } finally {
    setActiveLoaded(true);
  }
};

useEffect(() => {
  if (!isAuthenticated || !entitlementId) return;

  fetchCurrentStudent();
  fetchActiveAttempt(entitlementId);

  const refresh = () => fetchActiveAttempt(entitlementId);
  const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) refresh(); };
  const onVisibility = () => { if (!document.hidden) refresh(); };

  window.addEventListener("pageshow", onPageShow);   // back/forward cache restore
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("focus", refresh);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}, [entitlementId, isAuthenticated]);


  useEffect(() => {
    if (!isAuthenticated) {

        navigate(`/practice/login`);
      }

  }, [isAuthenticated]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const CARD_HEIGHT = "45px";
  const CARD_WIDTH = "250px";
  

    const topBarBg = useColorModeValue(TOP_BAR_BG.light, TOP_BAR_BG.dark);

    const textColor = useColorModeValue(TEXT_COLORS.light, TEXT_COLORS.dark);  




 


  return (
<Box maxW="1000px" mx="auto" p={8}>

  <Box
    bg={cardBg}
    borderRadius="xl"
    borderWidth="1px"
    p={8}
  >

    <Text
      fontSize="3xl"
      fontWeight="bold"
      mb={2}
    >
      {assessmentLabel}
    </Text>

    <Badge
      colorScheme={
        assessmentType === "MOCK_EXAM"
          ? "purple"
          : timerEnabled
          ? "orange"
          : "green"
      }
      mb={6}
    >
      {assessmentLabel}
    </Badge>

    <Text
      fontWeight="bold"
      mb={3}
    >
      Important Instructions
    </Text>

    <Box pl={5}>

      <Text mb={2}>
        • Only one assessment attempt can remain active at a time.
      </Text>

      <Text mb={2}>
        • Closing the attempt window does not automatically submit the attempt.
      </Text>

      <Text mb={2}>
        • Eduvic automatically restores attempts that were previously left in progress.
      </Text>

      <Text mb={2}>
        • Timed assessments continue running even if the attempt window is closed.
      </Text>

      <Text mb={2}>
        • Timed assessments are automatically submitted once the allotted time expires.
      </Text>

      <Text mb={2}>
        • Untimed assessments remain available until your entitlement access expires.
      </Text>

      {assessmentType === "MOCK_EXAM" && (
        <Text mb={2}>
          • Mock Exams may contain multiple Question Sets separated by scheduled break intervals.
        </Text>
      )}

      {assessmentType === "MOCK_EXAM" && (
        <Text mb={2}>
          • Subsequent Question Sets may automatically begin according to the Mock Exam schedule.
        </Text>
      )}

      <Text mb={2}>
        • Attempts can be submitted early from inside the Practice Attempt Lobby.
      </Text>

    </Box>

    <Box mt={10}>

      {activeLoaded && !activeAttempt && (

        <Button
          size="lg"
          colorScheme="blue"
          leftIcon={<FaHourglassStart />}
          onClick={handleStartOrResume}
        >
          Start Attempt
        </Button>

      )}

      {isSameAttemptInProgress && (

        <Button
          size="lg"
          colorScheme="green"
          leftIcon={<FaFlag />}
          onClick={handleStartOrResume}
        >
          Resume Attempt
        </Button>

      )}

      {hasAnotherAttemptInProgress && (

        <Box>

          <Badge
            colorScheme="orange"
            mb={4}
          >
            Another Attempt In Progress
          </Badge>

          <Text mb={5}>
            You currently have another active assessment attempt.
            Because only one assessment attempt can remain active at a time,
            you must resume or complete that attempt before starting a new one.
          </Text>

          <Button
            colorScheme="orange"
            size="lg"
            onClick={handleResumeOtherAttempt}
          >
            Resume Existing Attempt
          </Button>

        </Box>

      )}

    </Box>

  </Box>

</Box>
    
  );
};

export default StudentAttemptInstructionStart;
